import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Share2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SharedUsersList } from "./SharedUsersList";

interface ShareDialogProps {
  itemId: number;
  itemType: 'asset' | 'place' | 'company';
  onShare?: () => void;
}

export function ShareDialog({ itemId, itemType, onShare }: ShareDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleShare = async () => {
    if (!email) {
      toast({
        variant: "destructive",
        title: "Email required",
        description: "Please enter an email address.",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // First find the user by email - using limit(1) instead of single()
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .ilike('email', email.toLowerCase())
        .limit(1);

      if (profileError) throw profileError;
      
      if (!profiles || profiles.length === 0) {
        toast({
          variant: "destructive",
          title: "User not found",
          description: "No user found with this email address. They need to sign up first.",
        });
        return;
      }

      const shareData = {
        [`${itemType}_id`]: itemId,
        shared_by: user.id,
        shared_type: itemType,
        shared_with: profiles[0].id,
      };

      const { error } = await supabase
        .from("shared_assets")
        .insert(shareData);

      if (error) throw error;

      toast({
        title: "Success",
        description: `${itemType} shared successfully`,
      });
      
      setEmail("");
      if (onShare) onShare();
    } catch (error: any) {
      console.error('Share error:', error);
      toast({
        variant: "destructive",
        title: `Error sharing ${itemType}`,
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) setEmail("");
      }}
    >
      <Button 
        variant="outline" 
        size="sm"
        className="w-full"
        onClick={() => setIsOpen(true)}
      >
        <Share2 className="mr-2 h-4 w-4" />
        Share
      </Button>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share {itemType}</DialogTitle>
          <DialogDescription>
            Enter the email address of the user you want to share this {itemType} with.
            They must have an account to receive the share.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Share with Email</label>
            <Input
              type="email"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button
            onClick={handleShare}
            disabled={loading}
            className="w-full"
          >
            {loading ? "Sharing..." : `Share ${itemType}`}
          </Button>

          <div className="pt-4 border-t">
            {itemType === 'asset' && <SharedUsersList assetId={itemId} />}
            {itemType === 'place' && <SharedUsersList placeId={itemId} />}
            {itemType === 'company' && <SharedUsersList companyId={itemId} />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}